/**
 * Esquema de Rich Content para mensajes de Caonabo IA.
 *
 * El backend infiere el tipo de rich content basándose en la TOOL ejecutada
 * y su output. NO requiere que el LLM lo genere — funciona transparente.
 *
 * Reglas heurísticas:
 *   - Tool de búsqueda con N>1 resultados → RichTable
 *   - Tool ver_X o detail con 1 resultado → RichDetail
 *   - Tool con pending_confirmation: true → RichConfirm
 *   - Tool write con ok: true → RichResult
 */

export type RichColumnFormat =
  | "money"
  | "currency"
  | "qq"
  | "date"
  | "datetime"
  | "number"
  | "percent"
  | "status";

export type RichTable = {
  type: "table";
  toolName: string;
  title: string;
  /** Subtítulo opcional debajo del título (ej: "Top 10 de 50"). */
  subtitle?: string;
  columns: Array<{ key: string; label: string; format?: RichColumnFormat }>;
  rows: Record<string, unknown>[];
  totalCount?: number;
  /** Resumen agregado mostrado como strip arriba de la tabla (totales, promedios). */
  summary?: Array<{ key: string; label: string; value: string | number; format?: RichColumnFormat }>;
};

export type RichDetail = {
  type: "detail";
  toolName: string;
  title: string;
  fields: Array<{ key: string; label: string; value: unknown; format?: "money" | "qq" | "date" | "datetime" | "status" }>;
  /** Sub-tabla opcional (ej: líneas de OA, pagos de CxP). */
  subtable?: RichTable;
};

export type RichResult = {
  type: "result";
  toolName: string;
  ok: boolean;
  title: string;
  summary?: string;
  /** Pares clave-valor del resultado (ej: id creado, número generado). */
  fields?: Array<{ key: string; label: string; value: unknown }>;
};

export type RichConfirm = {
  type: "confirm";
  toolName: string;
  /** Snapshot exacto del input que se ejecutará en la 2da llamada. */
  pendingInput: Record<string, unknown>;
  prompt: string;
  summary: Record<string, unknown>;
  warning?: string;
};

/** Gráfica generada en tiempo real. Renderizada con QuickChart.io. */
export type RichChart = {
  type: "chart";
  toolName: string;
  chartType: "bar" | "horizontalBar" | "line" | "pie" | "doughnut" | "radar" | "scatter";
  title: string;
  subtitle?: string;
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string | string[];
  }>;
};

/** Mapa interactivo con marcadores y/o ruta. */
export type RichMap = {
  type: "map";
  toolName: string;
  title?: string;
  /** Marcadores en el mapa. Mínimo 1. */
  markers: Array<{
    lat: number;
    lng: number;
    label?: string;
    color?: "red" | "blue" | "green" | "orange" | "purple";
  }>;
  /** Ruta opcional como polyline encoded (Mapbox/Google) o array de [lat,lng]. */
  polyline?: string;
  routeCoords?: Array<[number, number]>;
  /** Zoom del mapa (1-20, default 14). */
  zoom?: number;
  /** Subtítulo opcional con info adicional (distancia, duración). */
  subtitle?: string;
};

export type RichContent = RichTable | RichDetail | RichResult | RichConfirm | RichMap | RichChart;

/* ─────────────── Heurísticas para inferir Rich Content ─────────────── */

/** Detecta si un output de tool es una respuesta pending_confirmation. */
function isPendingConfirmation(output: unknown): output is {
  pending_confirmation: true;
  confirmation_prompt: string;
  summary: Record<string, unknown>;
  warning?: string;
} {
  return (
    typeof output === "object" &&
    output !== null &&
    "pending_confirmation" in output &&
    (output as { pending_confirmation: unknown }).pending_confirmation === true
  );
}

/** Mapeo de tools a títulos legibles para rich content. */
const TITLE_MAP: Record<string, string> = {
  buscar_productor: "Productores",
  buscar_oa: "Órdenes de abastecimiento",
  buscar_siembra: "Siembras",
  buscar_recepcion: "Recepciones",
  buscar_viaje: "Viajes",
  buscar_acuerdo: "Acuerdos",
  buscar_cxp: "Cuentas por pagar",
  buscar_factura_proveedor: "Facturas de proveedores",
  buscar_factura_bravo: "Facturas Bravo",
  buscar_pedido_bravo: "Pedidos Bravo (semanales)",
  buscar_pedido_diario: "Pedidos Bravo (diarios)",
  buscar_lote_recepcion: "Lotes de recepción",
  ver_almacenes: "Almacenes",
  ver_stock_almacen: "Stock del almacén",
  alertas_stock_bajo: "Stock bajo",
  ver_productor: "Productor",
  ver_oa: "Orden de abastecimiento",
  ver_recepcion: "Recepción",
  ver_viaje: "Viaje",
  ver_acuerdo: "Acuerdo",
  ver_cxp: "Cuenta por pagar",
  ver_pedido_bravo: "Pedido Bravo",
  ver_lote_recepcion: "Lote de recepción",
  resumen_finanzas_hoy: "Resumen financiero",
  demanda_pedidos_dia: "Demanda del día",
};

/** Esquemas de columnas predefinidos por tool de búsqueda. */
const TABLE_SCHEMAS: Record<
  string,
  { rowsKey: string; columns: RichTable["columns"] }
> = {
  buscar_productor: {
    rowsKey: "productores",
    columns: [
      { key: "nombre", label: "Nombre" },
      { key: "tipo", label: "Tipo" },
      { key: "zona", label: "Zona" },
      { key: "telefono", label: "Tel." },
    ],
  },
  buscar_oa: {
    rowsKey: "ordenes",
    columns: [
      { key: "numero", label: "OA" },
      { key: "estado", label: "Estado", format: "status" },
      { key: "productor", label: "Productor" },
      { key: "fechaLlegada", label: "Llegada", format: "datetime" },
    ],
  },
  buscar_siembra: {
    rowsKey: "siembras",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "cultivo", label: "Cultivo" },
      { key: "productor", label: "Productor" },
      { key: "estado", label: "Estado", format: "status" },
      { key: "fechaSiembra", label: "Fecha", format: "date" },
    ],
  },
  buscar_recepcion: {
    rowsKey: "recepciones",
    columns: [
      { key: "ordenNumero", label: "OA" },
      { key: "productor", label: "Productor" },
      { key: "estado", label: "Estado", format: "status" },
      { key: "pesoNetoKg", label: "Peso (kg)" },
      { key: "montoCalculado", label: "Monto", format: "money" },
    ],
  },
  buscar_viaje: {
    rowsKey: "viajes",
    columns: [
      { key: "id", label: "#" },
      { key: "estado", label: "Estado", format: "status" },
      { key: "conductor", label: "Conductor" },
      { key: "vehiculo", label: "Vehículo" },
      { key: "fechaSalidaProg", label: "Salida", format: "datetime" },
    ],
  },
  buscar_acuerdo: {
    rowsKey: "acuerdos",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "productor", label: "Productor" },
      { key: "nivelNombre", label: "Nivel" },
      { key: "estado", label: "Estado", format: "status" },
      { key: "saldoDisponible", label: "Saldo", format: "money" },
    ],
  },
  buscar_cxp: {
    rowsKey: "cxps",
    columns: [
      { key: "numero", label: "Número" },
      { key: "productor", label: "Productor" },
      { key: "saldoPendiente", label: "Saldo", format: "money" },
      { key: "estado", label: "Estado", format: "status" },
      { key: "fechaVencimiento", label: "Vence", format: "date" },
    ],
  },
  buscar_factura_proveedor: {
    rowsKey: "facturas",
    columns: [
      { key: "ncf", label: "NCF" },
      { key: "productor", label: "Productor" },
      { key: "total", label: "Total", format: "money" },
      { key: "estadoECF", label: "e-CF", format: "status" },
    ],
  },
  buscar_pedido_bravo: {
    rowsKey: "pedidos",
    columns: [
      { key: "fecha", label: "Fecha", format: "date" },
      { key: "producto", label: "Producto" },
      { key: "volumenLb", label: "Volumen lb" },
      { key: "estado", label: "Estado", format: "status" },
    ],
  },
  buscar_pedido_diario: {
    rowsKey: "pedidos",
    columns: [
      { key: "ordenCompra", label: "OC" },
      { key: "fechaEntrega", label: "Entrega", format: "date" },
      { key: "totalBultos", label: "Bultos" },
      { key: "totalNeto", label: "Neto", format: "money" },
    ],
  },
  buscar_lote_recepcion: {
    rowsKey: "lotes",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "producto", label: "Producto" },
      { key: "productor", label: "Productor" },
      { key: "estado", label: "Estado", format: "status" },
      { key: "kgBuenoTotal", label: "Bueno (kg)" },
    ],
  },
  ver_almacenes: {
    rowsKey: "almacenes",
    columns: [
      { key: "nombre", label: "Nombre" },
      { key: "tipo", label: "Tipo" },
      { key: "ubicacion", label: "Ubicación" },
      { key: "capacidadLb", label: "Capacidad lb" },
    ],
  },
  ver_stock_almacen: {
    rowsKey: "items",
    columns: [
      { key: "producto", label: "Producto" },
      { key: "cantidadLb", label: "Cant. lb" },
      { key: "cantidadQq", label: "qq" },
      { key: "stockMinimo", label: "Mínimo" },
    ],
  },
  alertas_stock_bajo: {
    rowsKey: "items",
    columns: [
      { key: "almacen", label: "Almacén" },
      { key: "producto", label: "Producto" },
      { key: "cantidadLb", label: "Actual" },
      { key: "stockMinimo", label: "Mínimo" },
      { key: "deficitLb", label: "Déficit" },
    ],
  },
};

/**
 * Genera RichContent a partir del nombre de tool y su output.
 * Devuelve null si no aplica (rendering como texto plano).
 */
export function inferRichContent(args: {
  toolName: string;
  input: Record<string, unknown>;
  output: unknown;
}): RichContent | null {
  const { toolName, input, output } = args;

  // Tools INVISIBLES: nunca generan RichContent. Son llamadas internas
  // que el LLM hace por detrás (consultas de memoria, introspección de
  // schema). Mostrarlas al usuario distrae sin agregar valor.
  if (
    toolName === "recordar_contexto" ||
    toolName === "describir_modelos" ||
    toolName === "describir_campos" ||
    toolName === "describir_relaciones"
  ) {
    return null;
  }

  // 1. pending_confirmation → RichConfirm
  if (isPendingConfirmation(output)) {
    return {
      type: "confirm",
      toolName,
      pendingInput: input,
      prompt: output.confirmation_prompt,
      summary: output.summary,
      warning: output.warning,
    };
  }

  // 1a. generar_grafica → RichChart
  if (
    toolName === "generar_grafica" &&
    typeof output === "object" &&
    output !== null &&
    "_richChart" in output
  ) {
    const c = (output as { _richChart: Record<string, unknown> })._richChart;
    return {
      type: "chart",
      toolName,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      chartType: c.chartType as any,
      title: String(c.title ?? "Gráfica"),
      subtitle: c.subtitle ? String(c.subtitle) : undefined,
      labels: Array.isArray(c.labels) ? c.labels.map(String) : [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      datasets: (c.datasets as any[]) ?? [],
    };
  }

  // 1b. Tools de mapas → RichMap
  const mapResult = inferMapFromTool(toolName, output);
  if (mapResult) return mapResult;

  // 2. Tool de búsqueda con schema definido → RichTable
  const tableSchema = TABLE_SCHEMAS[toolName];
  if (tableSchema && typeof output === "object" && output !== null) {
    const rows = (output as Record<string, unknown>)[tableSchema.rowsKey];
    if (Array.isArray(rows) && rows.length > 0) {
      const totalCount = (output as { count?: number }).count ?? rows.length;
      return {
        type: "table",
        toolName,
        title: TITLE_MAP[toolName] ?? toolName,
        columns: tableSchema.columns,
        rows: rows as Record<string, unknown>[],
        totalCount,
      };
    }
  }

  // 3. Tool de detalle (ver_X) con resultado → RichDetail
  if (toolName.startsWith("ver_") || toolName === "resumen_finanzas_hoy" || toolName === "demanda_pedidos_dia") {
    if (
      typeof output === "object" &&
      output !== null &&
      !("error" in output) &&
      Object.keys(output).length > 0
    ) {
      const obj = output as Record<string, unknown>;
      const fields = Object.entries(obj)
        .filter(([k, v]) => {
          // Skip arrays grandes (los rendereamos como subtabla aparte si aplica)
          if (Array.isArray(v) && v.length > 0 && typeof v[0] === "object") return false;
          // Skip null/undefined
          if (v === null || v === undefined) return false;
          // Skip nested objects complejos
          if (typeof v === "object" && !Array.isArray(v)) return false;
          return k !== "error";
        })
        .slice(0, 20)
        .map(([k, v]) => ({ key: k, label: humanize(k), value: v }));
      if (fields.length === 0) return null;
      return {
        type: "detail",
        toolName,
        title: TITLE_MAP[toolName] ?? toolName,
        fields,
      };
    }
  }

  // 4. Tool write con ok: true → RichResult
  if (
    typeof output === "object" &&
    output !== null &&
    "ok" in output &&
    (output as { ok: boolean }).ok === true
  ) {
    const obj = output as Record<string, unknown>;
    const fields = Object.entries(obj)
      .filter(([k]) => k !== "ok" && k !== "nota" && k !== "note")
      .slice(0, 8)
      .map(([k, v]) => ({
        key: k,
        label: humanize(k),
        value: typeof v === "object" ? JSON.stringify(v) : v,
      }));
    return {
      type: "result",
      toolName,
      ok: true,
      title: "Operación completada",
      summary: typeof obj.nota === "string" ? obj.nota : undefined,
      fields,
    };
  }

  return null;
}

function humanize(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Detecta si un output de tool tiene coords y devuelve un RichMap. */
function inferMapFromTool(toolName: string, output: unknown): RichMap | null {
  if (!output || typeof output !== "object") return null;
  const o = output as Record<string, unknown>;

  // geocode_directo / geocode_inverso → 1 marker
  if ((toolName === "geocode_directo" || toolName === "geocode_inverso") && o.ok === true) {
    const lat = Number(o.latitud);
    const lng = Number(o.longitud);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return {
        type: "map",
        toolName,
        title: (o.direccionFormateada as string) ?? "Ubicación",
        markers: [{ lat, lng, color: "red", label: (o.direccionFormateada as string)?.slice(0, 30) }],
        zoom: 14,
      };
    }
  }

  // distancia_ruta / mapbox_directions → 2 markers + ruta
  if (toolName === "distancia_ruta" || toolName === "mapbox_directions") {
    if (o.ok === true) {
      const origen = o.origen as { lat?: number; lng?: number } | undefined;
      const destino = o.destino as { lat?: number; lng?: number } | undefined;
      if (origen && destino && Number.isFinite(origen.lat) && Number.isFinite(destino.lat)) {
        const subtitle = [
          o.distancia ? (o.distancia as { texto: string }).texto : null,
          o.duracion ? (o.duracion as { texto: string }).texto : null,
          typeof o.distanciaKm === "number" ? `${o.distanciaKm} km` : null,
          typeof o.duracionMinutos === "number" ? `${o.duracionMinutos} min` : null,
        ].filter(Boolean).join(" · ");
        return {
          type: "map",
          toolName,
          title: "Ruta",
          subtitle,
          markers: [
            { lat: origen.lat!, lng: origen.lng!, color: "green", label: "Origen" },
            { lat: destino.lat!, lng: destino.lng!, color: "red", label: "Destino" },
          ],
          polyline: typeof o.polyline === "string" ? o.polyline : undefined,
          zoom: 11,
        };
      }
    }
  }

  // mapa_estatico_url → ya tiene URL, mostramos el marker
  if (toolName === "mapa_estatico_url" && o.ok === true) {
    const lat = Number(o.latitud);
    const lng = Number(o.longitud);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return {
        type: "map",
        toolName,
        markers: [{ lat, lng, color: "red" }],
        zoom: Number(o.zoom ?? 15),
      };
    }
  }

  // ver_productor / ver_finca / ver_lote / ver_oa → si tiene coords, mostrar mapa
  if (toolName.startsWith("ver_")) {
    const candidatos: Array<[unknown, unknown]> = [
      [o.latRegistro, o.lngRegistro],
      [o.latitud, o.longitud],
    ];
    // Buscar también en sub-objetos comunes (productor, finca, lote, ubicacion)
    for (const subKey of ["productor", "finca", "lote", "ubicacion"]) {
      const sub = o[subKey] as Record<string, unknown> | undefined;
      if (sub && typeof sub === "object") {
        candidatos.push([sub.latRegistro, sub.lngRegistro]);
        candidatos.push([sub.latitud, sub.longitud]);
      }
    }
    for (const [lat, lng] of candidatos) {
      const la = Number(lat);
      const ln = Number(lng);
      if (Number.isFinite(la) && Number.isFinite(ln) && la !== 0 && ln !== 0) {
        return {
          type: "map",
          toolName,
          title: "Ubicación",
          markers: [{ lat: la, lng: ln, color: "red" }],
          zoom: 14,
        };
      }
    }
  }

  // ver_viaje SOLO genera mapa si hay UNA parada (centro del viaje).
  // Cuando hay múltiples paradas NO inferimos mapa automático aquí,
  // porque conectarlas con líneas rectas es engañoso. En su lugar el LLM
  // debe llamar distancia_ruta con waypoints para ruta REAL por carretera.
  if (toolName === "ver_viaje" && Array.isArray(o.paradas)) {
    const paradas = o.paradas as Array<{ lat?: number; lng?: number; descripcion?: string; tipo?: string }>;
    const conCoords = paradas.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (conCoords.length === 1) {
      const p = conCoords[0];
      return {
        type: "map",
        toolName,
        title: "Parada del viaje",
        markers: [{ lat: p.lat as number, lng: p.lng as number, label: p.descripcion?.slice(0, 25), color: "blue" }],
        zoom: 13,
      };
    }
  }

  return null;
}

/**
 * Deduplica mapas que muestran lo mismo (mismo set de markers).
 * Si llegan dos mapas con markers idénticos, mantiene el que tiene polyline
 * (ruta real) sobre el que no la tiene. Útil cuando varias tools generan
 * mapas redundantes en el mismo turno.
 */
export function dedupRichMaps(items: RichContent[]): RichContent[] {
  const out: RichContent[] = [];
  const mapKeyToIdx = new Map<string, number>();
  for (const item of items) {
    if (item.type !== "map") {
      out.push(item);
      continue;
    }
    const key = item.markers
      .map((m) => `${m.lat.toFixed(3)},${m.lng.toFixed(3)}`)
      .sort()
      .join("|");
    const prevIdx = mapKeyToIdx.get(key);
    if (prevIdx === undefined) {
      mapKeyToIdx.set(key, out.length);
      out.push(item);
    } else {
      // Ya hay un mapa con estos mismos markers. Si el nuevo tiene polyline
      // (ruta real) y el anterior no, reemplazar.
      const prev = out[prevIdx] as RichMap;
      if (item.polyline && !prev.polyline) {
        out[prevIdx] = item;
      }
      // Si ambos tienen polyline o ninguno, conservar el primero (no agregar duplicado).
    }
  }
  return out;
}
